// libraries
// react
import type { Control, FieldPath } from 'react-hook-form';
import { Controller } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, InputAdornment, Stack, TextField } from '@mui/material';

// shared components
// shared-components
import {
  RHFCheckbox,
  RHFFieldLabel,
  RHFNumberField,
  RHFSelect,
  RHFTextField,
} from '@/components/ui/form';

// relative
import { getTypeLabel } from '../schemaUtils';
import type { JsonSchemaNode } from '../types';
import type { AlarmConfigurationFormValues } from './alarmConfigurationForm.schema';

type RHFJsonSchemaFieldsProps = {
  schema: JsonSchemaNode;
  control: Control<AlarmConfigurationFormValues>;
  alarmType: string;
  fieldPathPrefix?: string[];
  translationPathPrefix?: string[];
  /** Fields matching this predicate (by translation path, e.g. `['threshold']`)
   * are displayed/edited as a 0-100 percentage while stored as a 0-1 ratio. */
  isPercentageField?: (translationPath: string[]) => boolean;
};

/**
 * Recursively renders one RHF field per leaf of a backend-provided JSON
 * Schema (`AlarmTypeDto.configurationSchema`), bound to
 * `configuration.<dot.path>` on the shared alarm configuration form control.
 * Shared by every alarm type's create/edit form so field rendering and
 * translation-key resolution only need to be written once.
 */
export function RHFJsonSchemaFields({
  schema,
  control,
  alarmType,
  fieldPathPrefix = ['configuration'],
  translationPathPrefix = [],
  isPercentageField = () => false,
}: RHFJsonSchemaFieldsProps) {
  const entries = Object.entries(schema.properties ?? {});
  const requiredFields = new Set(schema.required ?? []);
  const { t, i18n } = useTranslation('alarms');

  const buildPathVariants = (path: string[]) => {
    const normalizedSegments = path.map((segment) => {
      if (!segment) return segment;
      return `${segment.charAt(0).toLowerCase()}${segment.slice(1)}`;
    });

    const variants = [path.join('.'), normalizedSegments.join('.')];
    return Array.from(new Set(variants));
  };

  const resolveTranslatedConfigText = (
    path: string[],
    suffix: 'label' | 'description',
  ) => {
    const pathVariants = buildPathVariants(path);

    for (const pathKey of pathVariants) {
      const byTypeKey = `configurations.byAlarmType.${alarmType}.${pathKey}.${suffix}`;
      const commonKey = `configurations.common.${pathKey}.${suffix}`;

      if (i18n.exists(byTypeKey, { ns: 'alarms' })) {
        return t(byTypeKey);
      }

      if (i18n.exists(commonKey, { ns: 'alarms' })) {
        return t(commonKey);
      }
    }

    return undefined;
  };

  const resolveEnumOptionLabel = (path: string[], optionValue: unknown) => {
    const normalizedOptionKey = String(optionValue).toLowerCase();
    const pathVariants = buildPathVariants(path);

    for (const pathKey of pathVariants) {
      const byTypeKey = `configurations.byAlarmType.${alarmType}.${pathKey}.options.${normalizedOptionKey}`;
      const commonKey = `configurations.common.${pathKey}.options.${normalizedOptionKey}`;

      if (i18n.exists(byTypeKey, { ns: 'alarms' })) {
        return t(byTypeKey);
      }

      if (i18n.exists(commonKey, { ns: 'alarms' })) {
        return t(commonKey);
      }
    }

    return String(optionValue);
  };

  return (
    <Stack spacing={2}>
      {entries.map(([key, childSchema]) => {
        const fieldPath = [...fieldPathPrefix, key];
        const translationPath = [...translationPathPrefix, key];
        const fieldName = fieldPath.join(
          '.',
        ) as FieldPath<AlarmConfigurationFormValues>;
        const translatedLabel =
          resolveTranslatedConfigText(translationPath, 'label') || key;
        const translatedDescription = resolveTranslatedConfigText(
          translationPath,
          'description',
        );
        const isRequired = requiredFields.has(key);

        if (childSchema.type === 'object' || childSchema.properties) {
          return (
            <Box key={key}>
              <RHFJsonSchemaFields
                schema={childSchema}
                control={control}
                alarmType={alarmType}
                fieldPathPrefix={fieldPath}
                translationPathPrefix={translationPath}
                isPercentageField={isPercentageField}
              />
            </Box>
          );
        }

        const typeName = getTypeLabel(childSchema.type);

        if (childSchema.enum && childSchema.enum.length > 0) {
          return (
            <RHFSelect
              key={key}
              name={fieldName}
              control={control}
              fullWidth
              required={isRequired}
              label={translatedLabel}
              helperText={translatedDescription}
              options={childSchema.enum.map((enumValue) => ({
                value: String(enumValue),
                label: resolveEnumOptionLabel(translationPath, enumValue),
              }))}
            />
          );
        }

        if (typeName === 'boolean') {
          return (
            <RHFCheckbox
              key={key}
              name={fieldName}
              control={control}
              label={translatedLabel}
            />
          );
        }

        if (
          isPercentageField(translationPath) &&
          (typeName === 'number' || typeName === 'integer')
        ) {
          return (
            <Controller
              key={key}
              name={fieldName}
              control={control}
              render={({ field, fieldState }) => {
                const numericValue =
                  typeof field.value === 'number' ? field.value * 100 : '';

                return (
                  <Box>
                    <RHFFieldLabel htmlFor={fieldName} required={isRequired}>
                      {translatedLabel}
                    </RHFFieldLabel>
                    <TextField
                      id={fieldName}
                      fullWidth
                      required={isRequired}
                      variant="outlined"
                      type="number"
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ?? translatedDescription
                      }
                      value={numericValue}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      onChange={(event) => {
                        const rawValue = event.target.value;
                        field.onChange(
                          rawValue === '' ? null : Number(rawValue) / 100,
                        );
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        },

                        htmlInput: { min: 0, max: 100, step: 1 }
                      }} />
                  </Box>
                );
              }}
            />
          );
        }

        if (typeName === 'number' || typeName === 'integer') {
          return (
            <RHFNumberField
              key={key}
              name={fieldName}
              control={control}
              fullWidth
              required={isRequired}
              label={translatedLabel}
              helperText={translatedDescription}
            />
          );
        }

        return (
          <RHFTextField
            key={key}
            name={fieldName}
            control={control}
            fullWidth
            required={isRequired}
            label={translatedLabel}
            helperText={translatedDescription}
          />
        );
      })}
    </Stack>
  );
}
